import { adminFetch } from '../../lib/adminFetch';
import React, { useState, useEffect } from 'react';

const MLAdmin = ({ initialTab = 'lists' }) => {
  const [customers, setCustomers] = useState([]);
  const [mlLists, setMlLists] = useState([]);
  const [dragIndex, setDragIndex] = useState(null);
  const [history, setHistory] = useState([]);
  const tab = initialTab;
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);

  // 送信フォーム
  const [selectedListId, setSelectedListId] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [confirmTargets, setConfirmTargets] = useState(null);

  // 履歴詳細
  const [viewHistory, setViewHistory] = useState(null);

  // 下書き
  const [drafts, setDrafts] = useState([]);
  const [draftName, setDraftName] = useState('');
  const [showDraftSave, setShowDraftSave] = useState(false);

  // リスト詳細表示
  const [viewListId, setViewListId] = useState('');

  // 新規リスト作成
  const [showNewForm, setShowNewForm] = useState(false);
  const [newListName, setNewListName] = useState('');

  // リスト編集
  const [editListId, setEditListId] = useState(null);
  const [editListName, setEditListName] = useState('');
  const [addEmail, setAddEmail] = useState('');

  // 顧客編集モーダル
  const [editForm, setEditForm] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [events, setEvents] = useState([]);

  useEffect(() => {
    adminFetch('/api/admin-read.php?file=customers&t=' + Date.now()).then((r) => r.ok ? r.json() : []).then(setCustomers).catch(() => setCustomers([]));
    adminFetch('/api/admin-read.php?file=ml-lists&t=' + Date.now()).then((r) => r.ok ? r.json() : []).then(setMlLists).catch(() => setMlLists([]));
    adminFetch('/api/admin-read.php?file=events&t=' + Date.now()).then((r) => r.ok ? r.json() : []).then(setEvents).catch(() => setEvents([]));
    adminFetch('/api/admin-read.php?file=ml-history&t=' + Date.now()).then((r) => r.ok ? r.json() : []).then(setHistory).catch(() => setHistory([]));
    adminFetch('/api/admin-read.php?file=ml-drafts&t=' + Date.now()).then((r) => r.ok ? r.json() : []).then(setDrafts).catch(() => setDrafts([]));
  }, []);

  const getField = (c, key) => (c.fields || []).find((f) => f.key === key)?.value || '';

  // リストのメンバー（emailsからCRM顧客を引き当て）
  const getMembers = (list) => {
    if (!list) return [];
    return (list.emails || []).map((email) => {
      const c = customers.find((c) => c.email === email);
      return c || { name: '', email, fields: [] };
    });
  };

  const selectedList = mlLists.find((l) => l.id === viewListId);
  const composeList = mlLists.find((l) => l.id === selectedListId);

  // リスト保存
  const saveMlLists = async (data) => {
    try {
      await adminFetch('/api/admin-data.php', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ file: 'ml-lists', data }) });
      setMlLists(data);
    } catch {}
  };

  const createList = () => {
    if (!newListName.trim()) return;
    const newList = { id: String(Date.now()), name: newListName.trim(), emails: [], createdAt: new Date().toISOString() };
    saveMlLists([...mlLists, newList]);
    setNewListName('');
    setShowNewForm(false);
    setMessage(`「${newList.name}」を作成しました`);
  };

  const deleteList = (id) => {
    if (!window.confirm('このリストを削除しますか？')) return;
    saveMlLists(mlLists.filter((l) => l.id !== id));
    if (viewListId === id) setViewListId('');
    setMessage('削除しました');
  };

  // D&Dで順番変更
  const handleDragStart = (index) => setDragIndex(index);
  const handleDragOver = (e) => e.preventDefault();
  const handleDrop = (dropIndex) => {
    if (dragIndex === null || dragIndex === dropIndex) return;
    const reordered = [...mlLists];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(dropIndex, 0, moved);
    saveMlLists(reordered);
    setDragIndex(null);
  };

  const startEditList = (list) => {
    setEditListId(list.id);
    setEditListName(list.name);
  };

  const saveListName = () => {
    if (!editListName.trim() || !editListId) return;
    saveMlLists(mlLists.map((l) => l.id === editListId ? { ...l, name: editListName.trim() } : l));
    setEditListId(null);
    setMessage('リスト名を変更しました');
  };

  const addEmailToList = async (listId) => {
    if (!addEmail.trim()) return;
    const emails = addEmail.split(/[\n,;]/).map((e) => e.trim()).filter((e) => e && e.includes('@'));
    if (emails.length === 0) return;

    // MLリストに追加
    saveMlLists(mlLists.map((l) => {
      if (l.id !== listId) return l;
      const existing = new Set(l.emails || []);
      emails.forEach((e) => existing.add(e));
      return { ...l, emails: [...existing] };
    }));

    // CRM未登録のメールアドレスを自動登録
    const existingEmails = new Set(customers.map((c) => c.email?.toLowerCase()).filter(Boolean));
    const newCustomers = emails
      .filter((e) => !existingEmails.has(e.toLowerCase()))
      .map((e, i) => ({
        id: Date.now() + '_' + i,
        name: '',
        email: e,
        type: 'company',
        status: '見込み',
        memo: '',
        fields: [],
      }));

    if (newCustomers.length > 0) {
      const updated = [...customers, ...newCustomers];
      try {
        await adminFetch('/api/admin-data.php', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ file: 'customers', data: updated }) });
        setCustomers(updated);
      } catch {}
    }

    setAddEmail('');
    setMessage(`${emails.length}件追加しました${newCustomers.length > 0 ? `（${newCustomers.length}件を顧客に新規登録）` : ''}`);
  };

  const removeEmailFromList = (listId, email) => {
    saveMlLists(mlLists.map((l) => {
      if (l.id !== listId) return l;
      return { ...l, emails: (l.emails || []).filter((e) => e !== email) };
    }));
  };

  // 送信確認
  const prepareConfirm = () => {
    if (!subject.trim() || !body.trim()) { setMessage('件名と本文を入力してください'); return; }
    const emails = composeList?.emails || [];
    if (emails.length === 0) { setMessage('送信先がありません'); return; }
    setConfirmTargets(emails);
  };

  // 一斉送信
  const sendML = async () => {
    if (!confirmTargets) return;
    setSending(true);
    setMessage('');
    try {
      const formData = new FormData();
      formData.append('subject', subject);
      formData.append('body', body);
      formData.append('emails', JSON.stringify(confirmTargets));
      attachments.forEach((file) => formData.append('attachments[]', file));
      const res = await adminFetch('/api/admin-ml.php?action=send', { method: 'POST', body: formData });
      const data = await res.json();
      if (data.success) {
        setMessage(`${data.sentCount}件に送信しました${data.failCount > 0 ? `（${data.failCount}件失敗）` : ''}`);
        setSubject(''); setBody(''); setAttachments([]); setConfirmTargets(null);
        const hRes = await adminFetch('/api/admin-read.php?file=ml-history&t=' + Date.now());
        if (hRes.ok) setHistory(await hRes.json());
      } else {
        setMessage('送信に失敗しました: ' + (data.message || ''));
      }
    } catch { setMessage('送信に失敗しました'); }
    finally { setSending(false); }
  };

  // 顧客編集
  const openEditModal = (customer) => {
    if (!customer.id) {
      // CRM未登録の場合、メールアドレスから顧客を検索
      const found = customers.find((c) => c.email === customer.email);
      if (found) {
        setEditForm({ ...found, fields: [...(found.fields || [])] });
      }
      return;
    }
    setEditForm({ ...customer, fields: [...(customer.fields || [])] });
  };

  const saveCustomerEdit = async () => {
    if (!editForm) return;
    setEditSaving(true);
    try {
      const updated = customers.map((c) => c.id === editForm.id ? editForm : c);
      const res = await adminFetch('/api/admin-data.php', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ file: 'customers', data: updated }) });
      const result = await res.json();
      if (result.success) { setCustomers(updated); setEditForm(null); setMessage('保存しました'); }
    } catch {}
    setEditSaving(false);
  };

  // 下書き
  const saveDrafts = async (data) => {
    try {
      await adminFetch('/api/admin-data.php', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ file: 'ml-drafts', data }) });
      setDrafts(data);
    } catch {}
  };

  const saveDraft = () => {
    if (!draftName.trim() || (!subject.trim() && !body.trim())) return;
    const draft = { id: String(Date.now()), name: draftName.trim(), subject, body, createdAt: new Date().toISOString() };
    saveDrafts([...drafts, draft]);
    setDraftName('');
    setShowDraftSave(false);
    setMessage(`下書き「${draft.name}」を保存しました`);
  };

  const loadDraft = (draft) => {
    setSubject(draft.subject);
    setBody(draft.body);
    setMessage(`下書き「${draft.name}」を読み込みました`);
  };

  const deleteDraft = (id) => {
    saveDrafts(drafts.filter((d) => d.id !== id));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const d = new Date(dateStr);
    return d.toLocaleDateString('ja-JP') + ' ' + d.toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6">
        {tab === 'lists' ? 'MLリスト一覧' : tab === 'compose' ? 'ML一斉送信' : 'ML送信履歴'}
      </h2>

      {message && <div className="mb-4 px-4 py-2 bg-green-100 text-green-800 rounded-lg text-sm">{message}</div>}

      {/* リスト一覧 */}
      {tab === 'lists' && (
        <div className="flex gap-6">
          <div className="w-1/3">
            <button onClick={() => setShowNewForm(true)} className="w-full mb-3 bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-blue-700">+ 新規リスト作成</button>

            {showNewForm && (
              <div className="bg-white rounded-xl shadow p-4 mb-3">
                <input type="text" value={newListName} onChange={(e) => setNewListName(e.target.value)}
                  placeholder="リスト名を入力" className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm mb-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  onKeyDown={(e) => e.key === 'Enter' && createList()} autoFocus />
                <div className="flex gap-2">
                  <button onClick={createList} disabled={!newListName.trim()} className="bg-blue-600 text-white px-4 py-1.5 rounded text-xs font-bold hover:bg-blue-700 disabled:opacity-50">作成</button>
                  <button onClick={() => { setShowNewForm(false); setNewListName(''); }} className="bg-gray-200 text-gray-700 px-4 py-1.5 rounded text-xs font-bold hover:bg-gray-300">キャンセル</button>
                </div>
              </div>
            )}

            {mlLists.length === 0 ? (
              <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500 text-sm">リストがありません</div>
            ) : (
              <div className="bg-white rounded-xl shadow overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b text-xs text-gray-500 font-bold">{mlLists.length}件のリスト</div>
                <div className="divide-y max-h-[60vh] overflow-y-auto">
                  {mlLists.map((list, index) => (
                    <div key={list.id} onClick={() => setViewListId(list.id)}
                      draggable onDragStart={() => handleDragStart(index)} onDragOver={handleDragOver} onDrop={() => handleDrop(index)}
                      className={`px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer ${viewListId === list.id ? 'bg-blue-50' : ''} ${dragIndex === index ? 'opacity-50' : ''}`}>
                      {editListId === list.id ? (
                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                          <input type="text" value={editListName} onChange={(e) => setEditListName(e.target.value)}
                            className="flex-1 border border-gray-300 rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                            onKeyDown={(e) => e.key === 'Enter' && saveListName()} autoFocus />
                          <button onClick={saveListName} className="bg-blue-600 text-white px-2.5 py-1 rounded text-[10px] font-bold hover:bg-blue-700">保存</button>
                          <button onClick={() => setEditListId(null)} className="bg-gray-200 text-gray-600 px-2.5 py-1 rounded text-[10px] font-bold hover:bg-gray-300">取消</button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="font-bold text-sm">{list.name}</p>
                            <p className="text-xs text-gray-500 mt-0.5">{(list.emails || []).length}件</p>
                          </div>
                          <div className="flex gap-1.5" onClick={(e) => e.stopPropagation()}>
                            <button onClick={() => startEditList(list)} className="bg-gray-100 text-gray-600 px-2.5 py-1 rounded text-[10px] font-bold hover:bg-gray-200">名前変更</button>
                            <button onClick={() => deleteList(list.id)} className="bg-red-50 text-red-600 px-2.5 py-1 rounded text-[10px] font-bold hover:bg-red-100">削除</button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div className="flex-1">
            {selectedList ? (
              <div className="bg-white rounded-xl shadow overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between">
                  <span className="font-bold text-sm">{selectedList.name}</span>
                  <span className="text-xs text-gray-500">{(selectedList.emails || []).length}件</span>
                </div>

                {/* メンバー追加 */}
                <div className="px-4 py-3 border-b bg-gray-50 space-y-2">
                  <div className="flex gap-2">
                    <input type="text" value={addEmail} onChange={(e) => setAddEmail(e.target.value)}
                      placeholder="名前・メールで検索して追加、またはメールアドレスを直接入力"
                      className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                      onKeyDown={(e) => e.key === 'Enter' && addEmailToList(selectedList.id)} />
                    <button onClick={() => addEmailToList(selectedList.id)} className="bg-blue-500 text-white px-3 py-1.5 rounded text-xs font-bold hover:bg-blue-600">追加</button>
                  </div>
                  {addEmail.trim() && !addEmail.includes('@') && (() => {
                    const s = addEmail.toLowerCase();
                    const listEmails = new Set(selectedList.emails || []);
                    const matches = customers.filter((c) => c.email && !listEmails.has(c.email) &&
                      (c.name.toLowerCase().includes(s) || c.email.toLowerCase().includes(s) || getField(c, '会社名').toLowerCase().includes(s))
                    ).slice(0, 8);
                    return matches.length > 0 ? (
                      <div className="border border-gray-200 rounded bg-white max-h-40 overflow-y-auto">
                        {matches.map((c) => (
                          <button key={c.id} onClick={() => { setAddEmail(c.email); }}
                            className="w-full text-left px-3 py-1.5 hover:bg-blue-50 text-xs flex items-center justify-between border-b last:border-b-0">
                            <span><span className="font-bold">{c.name}</span> <span className="text-gray-400">{getField(c, '会社名')}</span></span>
                            <span className="text-gray-400">{c.email}</span>
                          </button>
                        ))}
                      </div>
                    ) : null;
                  })()}
                </div>

                <div className="max-h-[55vh] overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="text-left px-3 py-2">名前</th>
                        <th className="text-left px-3 py-2">会社名</th>
                        <th className="text-left px-3 py-2">メール</th>
                        <th className="text-left px-3 py-2">区分</th>
                        <th className="text-left px-3 py-2">ステータス</th>
                        <th className="px-3 py-2 w-16"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {getMembers(selectedList).map((c, i) => (
                        <tr key={i} className="hover:bg-gray-50 cursor-pointer" onClick={() => openEditModal(c)}>
                          <td className="px-3 py-2 text-blue-600 hover:underline">{c.name || '-'}</td>
                          <td className="px-3 py-2 text-gray-500">{getField(c, '会社名')}</td>
                          <td className="px-3 py-2 text-gray-500">{c.email}</td>
                          <td className="px-3 py-2">
                            {c.type && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${c.type === 'sr' ? 'bg-purple-100 text-purple-700' : 'bg-blue-100 text-blue-700'}`}>{c.type === 'sr' ? '社労士' : '企業'}</span>}
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex flex-wrap gap-0.5">
                              {(c.salesStatus || c.status) && <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${{ '見込み': 'bg-yellow-100 text-yellow-700', '商談中': 'bg-blue-100 text-blue-700', '休止': 'bg-gray-100 text-gray-500' }[c.salesStatus || c.status] || 'bg-gray-100 text-gray-600'}`}>{c.salesStatus || c.status}</span>}
                              {c.srwithContract === 'あり' && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-green-100 text-green-700">SrWith</span>}
                              {c.academyStatus === '受講中' && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">アカデミー</span>}
                            </div>
                          </td>
                          <td className="px-3 py-2 text-center" onClick={(e) => e.stopPropagation()}>
                            <button onClick={() => removeEmailFromList(selectedList.id, c.email)} className="bg-red-50 text-red-600 px-2 py-0.5 rounded text-[10px] font-bold hover:bg-red-100">削除</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="px-4 py-3 border-t">
                  <button onClick={() => { setSelectedListId(selectedList.id); window.location.hash = 'ml_compose'; }}
                    className="bg-blue-600 text-white px-4 py-2 rounded-lg text-xs font-bold hover:bg-blue-700">
                    このリストにメール送信
                  </button>
                </div>
              </div>
            ) : (
              <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">左のリストを選択してください</div>
            )}
          </div>
        </div>
      )}

      {/* メール作成 */}
      {tab === 'compose' && (
        <div className="bg-white rounded-xl shadow p-6">
          <div className="mb-5">
            <label className="block text-sm font-bold mb-2">送信先リスト</label>
            <div className="flex flex-wrap gap-2">
              {mlLists.map((list) => (
                <button key={list.id} onClick={() => setSelectedListId(list.id)}
                  className={`px-4 py-2 rounded-lg text-xs font-bold ${selectedListId === list.id ? 'bg-blue-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                  {list.name}（{(list.emails || []).length}件）
                </button>
              ))}
            </div>
            {composeList && <p className="mt-2 text-sm text-gray-500 font-bold">送信先: {(composeList.emails || []).length}件</p>}
          </div>

          <div className="mb-4">
            <label className="block text-sm font-bold mb-1">件名</label>
            <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="メールの件名"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div className="mb-4">
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-bold">本文</label>
              <div className="flex gap-1">
                {[
                  { tag: '{name}', label: '名前' },
                  { tag: '{company}', label: '会社名' },
                ].map((t) => (
                  <button key={t.tag} onClick={() => setBody((prev) => prev + t.tag)}
                    className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-[10px] font-bold hover:bg-gray-200">{t.label}</button>
                ))}
              </div>
            </div>
            <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={10} placeholder="メール本文を入力...&#10;&#10;{name} 様、{company} の件について..."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
          </div>
          <div className="mb-5">
            <label className="inline-flex items-center gap-1.5 cursor-pointer text-gray-500 hover:text-gray-700 text-sm">
              <span>📎</span><span className="text-xs font-bold">ファイルを添付</span><span className="text-[10px] text-gray-400 font-normal">（30MBまで）</span>
              <input type="file" multiple className="hidden" onChange={(e) => setAttachments((prev) => [...prev, ...Array.from(e.target.files)])} />
            </label>
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-1.5">
                {attachments.map((file, i) => (
                  <span key={i} className="inline-flex items-center gap-1 bg-gray-100 rounded px-2 py-1 text-xs text-gray-700">
                    📎 {file.name}<button onClick={() => setAttachments((prev) => prev.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600 font-bold ml-0.5">×</button>
                  </span>
                ))}
              </div>
            )}
          </div>
          <div className="flex items-center gap-3 mb-6">
            <button onClick={prepareConfirm} disabled={sending || !subject.trim() || !body.trim() || !selectedListId}
              className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50">送信確認</button>
            <button onClick={() => setShowDraftSave(true)} disabled={!subject.trim() && !body.trim()}
              className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-bold hover:bg-gray-300 disabled:opacity-50">下書き保存</button>
          </div>

          {showDraftSave && (
            <div className="mb-4 bg-gray-50 rounded-lg p-4 flex items-center gap-2">
              <input type="text" value={draftName} onChange={(e) => setDraftName(e.target.value)}
                placeholder="下書き名" className="flex-1 border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                onKeyDown={(e) => e.key === 'Enter' && saveDraft()} autoFocus />
              <button onClick={saveDraft} disabled={!draftName.trim()} className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50">保存</button>
              <button onClick={() => { setShowDraftSave(false); setDraftName(''); }} className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-bold hover:bg-gray-300">取消</button>
            </div>
          )}

          {drafts.length > 0 && (
            <div className="mb-6">
              <p className="text-sm font-bold text-gray-600 mb-2">下書き一覧</p>
              <div className="space-y-1.5">
                {drafts.map((d) => (
                  <div key={d.id} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-2">
                    <div className="min-w-0">
                      <p className="text-xs font-bold text-gray-700">{d.name}</p>
                      <p className="text-[10px] text-gray-400 truncate">{d.subject || '(件名なし)'}</p>
                    </div>
                    <div className="flex gap-1.5 shrink-0 ml-2">
                      <button onClick={() => loadDraft(d)} className="bg-blue-100 text-blue-700 px-3 py-1 rounded text-[10px] font-bold hover:bg-blue-200">読み込み</button>
                      <button onClick={() => deleteDraft(d.id)} className="bg-red-50 text-red-600 px-3 py-1 rounded text-[10px] font-bold hover:bg-red-100">削除</button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {confirmTargets && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
              <div className="bg-white rounded-xl shadow-xl p-6 max-w-lg w-full mx-4">
                <h3 className="font-bold text-lg mb-3">送信確認</h3>
                <div className="mb-3 text-sm">
                  <p><span className="font-bold">リスト:</span> {composeList?.name}</p>
                  <p className="mt-1"><span className="font-bold">件名:</span> {subject}</p>
                  <p className="mt-1"><span className="font-bold">送信先:</span> {confirmTargets.length}件</p>
                  {attachments.length > 0 && <p className="mt-1"><span className="font-bold">添付:</span> {attachments.map((f) => f.name).join(', ')}</p>}
                </div>
                <div className="max-h-40 overflow-y-auto border rounded-lg mb-4 p-2">
                  {confirmTargets.map((email, i) => <p key={i} className="text-xs text-gray-600 py-0.5">{email}</p>)}
                </div>
                <div className="flex gap-3">
                  <button onClick={sendML} disabled={sending} className="bg-red-600 text-white px-6 py-2 rounded-lg text-sm font-bold hover:bg-red-700 disabled:opacity-50">
                    {sending ? '送信中...' : `${confirmTargets.length}件に送信する`}
                  </button>
                  <button onClick={() => setConfirmTargets(null)} className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg text-sm font-bold hover:bg-gray-300">キャンセル</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 送信履歴 */}
      {tab === 'history' && (
        <div>
          {history.length === 0 ? (
            <div className="bg-white rounded-xl shadow p-8 text-center text-gray-500">送信履歴がありません</div>
          ) : (
            <div className="space-y-2">
              {[...history].reverse().map((h, i) => (
                <div key={i} onClick={() => setViewHistory(viewHistory === i ? null : i)}
                  className="bg-white rounded-xl shadow p-4 cursor-pointer hover:bg-gray-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <p className="font-bold text-sm">{h.subject}</p>
                      <div className="flex gap-1.5">
                        <span className="bg-green-100 text-green-700 px-2 py-0.5 rounded text-[10px] font-bold">成功 {h.sentCount}</span>
                        {h.failCount > 0 && <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded text-[10px] font-bold">失敗 {h.failCount}</span>}
                        {h.attachments && h.attachments.length > 0 && <span className="text-[10px] text-gray-400">📎 {h.attachments.length}</span>}
                      </div>
                    </div>
                    <span className="text-xs text-gray-400">{formatDate(h.date)}</span>
                  </div>
                  {viewHistory === i && (
                    <div className="mt-3 border-t pt-3">
                      <p className="text-sm whitespace-pre-wrap text-gray-700 bg-gray-50 rounded-lg p-4 mb-3">{h.body}</p>
                      {h.attachments && h.attachments.length > 0 && (
                        <div className="mb-3 flex flex-wrap gap-1.5">{h.attachments.map((name, j) => <span key={j} className="text-xs text-gray-500">📎 {name}</span>)}</div>
                      )}
                      <div>
                        <p className="text-xs font-bold text-gray-500 mb-1">送信先（{h.emails.length}件）</p>
                        <div className="max-h-40 overflow-y-auto bg-gray-50 rounded-lg p-3">
                          {h.emails.map((email, j) => <p key={j} className="text-xs text-gray-600 py-0.5">{email}</p>)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* 顧客編集モーダル */}
      {editForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-lg w-full mx-4 max-h-[85vh] overflow-y-auto">
            <h3 className="font-bold text-lg mb-4">顧客情報の編集</h3>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold mb-1">姓</label>
                  <input type="text" value={(editForm.name || '').split(/\s+/)[0] || ''} onChange={(e) => {
                    const mei = (editForm.name || '').split(/\s+/).slice(1).join(' ');
                    setEditForm({ ...editForm, name: (e.target.value + ' ' + mei).trim() });
                  }} className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
                <div>
                  <label className="block text-xs font-bold mb-1">名</label>
                  <input type="text" value={(editForm.name || '').split(/\s+/).slice(1).join(' ') || ''} onChange={(e) => {
                    const sei = (editForm.name || '').split(/\s+/)[0] || '';
                    setEditForm({ ...editForm, name: (sei + ' ' + e.target.value).trim() });
                  }} className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold mb-1">メールアドレス</label>
                <input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold mb-1">区分</label>
                  <select value={editForm.type} onChange={(e) => setEditForm({ ...editForm, type: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    <option value="company">企業</option><option value="sr">社労士</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold mb-1">営業</label>
                  <select value={editForm.salesStatus || editForm.status || '見込み'} onChange={(e) => setEditForm({ ...editForm, salesStatus: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    {['見込み', '商談中', '休止'].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold mb-1">SrWith契約</label>
                  <select value={editForm.srwithContract || 'なし'} onChange={(e) => setEditForm({ ...editForm, srwithContract: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    {['なし', 'あり'].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-bold mb-1">アカデミー</label>
                  <select value={editForm.academyStatus || '未受講'} onChange={(e) => setEditForm({ ...editForm, academyStatus: e.target.value })}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                    {['未受講', '受講中', '解約済み'].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
              </div>
              {/* 追加項目 */}
              {editForm.fields && editForm.fields.length > 0 && (
                <div>
                  <label className="text-xs font-bold mb-1 block">追加項目</label>
                  <div className="space-y-1.5">
                    {editForm.fields.map((field, i) => (
                      field.key === 'イベントID' ? (
                        <div key={i} className="flex gap-2 items-center">
                          <span className="w-1/3 text-xs font-bold text-gray-600">イベント</span>
                          <span className="flex-1 text-xs text-gray-700">{events.find((e) => e.id === field.value)?.name || field.value}</span>
                        </div>
                      ) : (
                        <div key={i} className="flex gap-2">
                          <input type="text" value={field.key} onChange={(e) => {
                            const fields = [...editForm.fields]; fields[i] = { ...fields[i], key: e.target.value }; setEditForm({ ...editForm, fields });
                          }} placeholder="項目名" className="w-1/3 border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500" />
                          <input type="text" value={field.value} onChange={(e) => {
                            const fields = [...editForm.fields]; fields[i] = { ...fields[i], value: e.target.value }; setEditForm({ ...editForm, fields });
                          }} placeholder="値" className="flex-1 border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-blue-500" />
                          <button onClick={() => setEditForm({ ...editForm, fields: editForm.fields.filter((_, j) => j !== i) })}
                            className="bg-red-50 text-red-600 px-2 py-1 rounded text-[10px] font-bold hover:bg-red-100">削除</button>
                        </div>
                      )
                    ))}
                  </div>
                </div>
              )}
              <button onClick={() => setEditForm({ ...editForm, fields: [...(editForm.fields || []), { key: '', value: '' }] })}
                className="text-xs text-blue-600 font-bold hover:underline">+ 項目追加</button>
              <div>
                <label className="block text-xs font-bold mb-1">メモ</label>
                <textarea value={editForm.memo} onChange={(e) => setEditForm({ ...editForm, memo: e.target.value })} rows={2}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
              </div>
              <div className="flex gap-3 pt-2">
                <button onClick={saveCustomerEdit} disabled={editSaving || !editForm.name}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg text-sm font-bold hover:bg-blue-700 disabled:opacity-50">
                  {editSaving ? '保存中...' : '保存'}
                </button>
                <button onClick={() => setEditForm(null)} className="bg-gray-200 text-gray-700 px-6 py-2 rounded-lg text-sm font-bold hover:bg-gray-300">キャンセル</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MLAdmin;
